// GradientCommand — commits a gradient to a raster layer.
// Stores before/after pixel patches for undo/redo round-trip.

import type { Document } from "../document/Document";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { BBox } from "../pixel/brush";
import type { Command } from "./Command";

export class GradientCommand implements Command {
  readonly kind = "gradient";

  constructor(
    private readonly layerId: string,
    private readonly bbox: BBox,
    private readonly before: Uint8ClampedArray,
    private readonly after: Uint8ClampedArray,
  ) {}

  apply(doc: Document): Document {
    this.writePatch(doc, this.after);
    return doc;
  }

  invert(_before: Document, _after: Document): Command {
    return new GradientCommand(this.layerId, this.bbox, this.after, this.before);
  }

  private writePatch(doc: Document, patch: Uint8ClampedArray): void {
    const layer = findRaster(doc.layers, this.layerId);
    if (!layer) return;
    const { x, y, w, h } = this.bbox;
    const data = layer.pixels.data;
    const fullWidth = layer.pixels.width;
    for (let row = 0; row < h; row++) {
      const dstOff = ((y + row) * fullWidth + x) * 4;
      const srcOff = row * w * 4;
      data.set(patch.subarray(srcOff, srcOff + w * 4), dstOff);
    }
    layer.pixels.markDirty();
  }
}
